import { motion } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { HERO_BG, waLink } from "../lib/data";

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0B0E]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Residencia con ventanales polarizados Querétaro"
          className="w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0E]/85 via-[#0A0B0E]/40 to-[#0A0B0E]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0E]/80 via-transparent to-transparent" />
      </div>

      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(212,175,55,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.4) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-40 pb-20 min-h-screen flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-3 mb-8 max-w-fit"
        >
          <span className="w-10 h-px bg-[#D4AF37]" />
          <span className="text-[#D4AF37] text-xs uppercase tracking-[0.35em]">
            Servicio profesional en Querétaro
          </span>
        </motion.div>

        <div className="max-w-5xl">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="font-display text-5xl sm:text-6xl lg:text-[7.5rem] leading-[0.95] tracking-tighter text-white"
          >
            Película y{" "}
            <span className="italic font-light text-[#D4AF37]">polarizados</span>
            <br />
            para tu espacio.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 max-w-2xl text-lg lg:text-xl text-white/65 font-light leading-relaxed"
          >
            Especialistas en{" "}
            <span className="text-white">película de polarizado en Querétaro</span>{" "}
            para hogares, oficinas, edificios, comercios y plantas industriales.
            Materiales certificados <em>3M · Solex · Llumar</em> y garantía por escrito.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-black font-medium rounded-full overflow-hidden hover:shadow-[0_0_40px_rgba(212,175,55,0.5)] transition-all"
            >
              <MessageCircle size={18} className="relative z-10" />
              <span className="relative z-10">Cotización gratis por WhatsApp</span>
              <span className="relative z-10 transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#servicios"
              data-testid="hero-see-services"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm uppercase tracking-[0.2em] transition-colors"
            >
              Ver servicios
              <ArrowDown size={14} />
            </a>
          </motion.div>
        </div>

        {/* Bottom info strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="absolute bottom-10 left-6 lg:left-12 right-6 lg:right-12 flex items-end justify-between gap-6 flex-wrap"
        >
          <div className="flex items-center gap-2 text-white/55 text-sm">
            <span className="inline-block w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
            Querétaro, Qro. · Centro, Corregidora, El Marqués, San Juan del Río
          </div>
          <div className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.25em] text-white/40">
            <span>🏭 Industrial</span>
            <span>🏢 Oficinas</span>
            <span>🏠 Residencial</span>
            <span>🏪 Comercios</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
